/**
 * customer-app · 静态运行配置（与 config-lib 互补：本文件管 app/打印/语言选择器等运行常量）
 *
 * 来源：js/config.js（已迁移）。
 *
 * 职责：局域网 IP 占位、语言选择器元数据、localStorage 键名、打印机示例配置等。
 *   多语言文案由 ui-labels.bundle + i18n.json 合并提供（getUiLabelsBundle）。
 *
 * 兼容契约（必须保留）：
 *   - `window.RestaurantAppConfig` 字段名 / 形状与原 IIFE 完全一致
 *     被 app.js / ui-controller.js / html-templates 取用：
 *       - LANG_PICKER（多处用）
 *       - ORDER_COOLDOWN_UNTIL_KEY / CUSTOMER_SESSION_STORAGE / CUSTOMER_MEAL_TAB_KEY
 *       - DEFAULT_PRINT_V2 / DEFAULT_PRINTER_ROUTING
 *       - getUiLabelsBundle / getReceiptDataBundle
 *   - `OS.registerModule('config', …)`
 *
 * 设计：
 *   - 复用 P5a-5 的 `UiLangCode` 与 P5a-2 的 `RestaurantOrderLib['ORDERS_KEY']` 类型
 *   - `RestaurantConstants` 形状由 js/constants.js 在运行时提供（外部依赖），用窄接口断言
 *   - getUiLabelsBundle / getReceiptDataBundle 返回的字段保持开放（业务可扩展）
 */

import type { UiLangCode } from './config-lib';
import type { RestaurantOrderLib } from './order-lib';

/* ─────────── 类型 ─────────── */

/** 语言选择器单项（旗帜 + 缩写）；code 必为 UiLangCode */
export interface LangPickerEntry {
  code: UiLangCode;
  /** 大写两/三字母简称，如 'DE' / 'JP' */
  abbr: string;
  /** 旗帜 SVG 相对路径，如 'assets/flags/de.svg' */
  flag: string;
}

/** STORAGE_KEYS_BASE 的字面量键名集（不含 ORDERS） */
export interface StorageKeysBase {
  MENU: 'restaurantMenu';
  MENU_PRESET_VERSION: 'restaurantMenuPresetVersion';
  PRINT_CONFIG: 'restaurantPrintConfig';
  PRINTER_ROUTING: 'restaurantPrinterRouting';
  NETWORK_CONFIG: 'restaurantNetworkConfig';
  RESTAURANT_INFO: 'restaurantInfo';
  UI_LANG: 'restaurantUiLang';
  PRINTER_SETTINGS: 'printerSettings';
  SYSTEM_SETTINGS: 'restaurantSystemSettings';
}

/** buildKeys 输出：base + 运行时注入的 ORDERS */
export interface StorageKeys extends StorageKeysBase {
  ORDERS: string;
}

/** 打印机示例映射（管理端示意，实际路由用 categoryToPrinterId） */
export interface PrinterMappingExample {
  hot: string;
  cold: string;
  drink: string;
  rice: string;
  noodle: string;
  snack: string;
}

/** 打印路由配置 */
export interface PrinterRouting {
  /** 'per_category' 按分类分发；将来可能扩展更多模式 */
  dispatchMode: 'per_category';
  /** category id → printer id */
  categoryToPrinterId: Record<string, string>;
}

export interface PrintV2Printer {
  id: string;
  name: string;
  /** '58' / '80'（mm） */
  paperWidth: string;
  autoPrint: boolean;
  header: string;
  footer: string;
}

export interface PrintV2OrderReceipt {
  receiptLang: UiLangCode;
  /** 第二语言；'' 表示不打印第二语言 */
  receiptLangSecondary: UiLangCode | '';
  /** 收据字体预设；与 RECEIPT_FONT_CSS 键名对齐 */
  fontPreset: 'mono' | 'sans' | 'large';
}

export interface PrintV2 {
  printers: PrintV2Printer[];
  activePrinterId: string;
  orderReceipt: PrintV2OrderReceipt;
}

/** ui-labels.bundle 的输出形状（业务可扩展键） */
export type UiLabelsBundle = Record<UiLangCode, Record<string, unknown>>;

/** app-receipt-data 的输出形状（业务可扩展键） */
export interface ReceiptDataBundle {
  RECEIPT_LOCALE: Record<string, string>;
  RECEIPT_LABELS: Record<string, Record<string, unknown>>;
  RECEIPT_FONT_CSS: { mono: string; sans: string; large: string };
}

/** 外部 js/constants.js 提供的运行时常量（窄接口） */
interface RestaurantConstants {
  OS_LAN_IPV4?: string;
  DEFAULT_HTTP_PORT?: number | string;
  /** Bondrucker RAW 默认 IP（浏览器端与 printers.config / 环境变量呼应） */
  DEFAULT_BOND_PRINTER_IP?: string;
  DEFAULT_BOND_PRINTER_PORT?: number | string;
}

export interface RestaurantAppConfig {
  OS_LAN_IPV4: string;
  getDefaultThermalPrintEndpoint(): string;
  LANG_PICKER: readonly LangPickerEntry[];
  CUSTOMER_SESSION_STORAGE: string;
  ORDER_COOLDOWN_UNTIL_KEY: string;
  CUSTOMER_MEAL_TAB_KEY: string;
  DEFAULT_CUSTOMER_GUEST_NAME: string;
  GUEST_NAME_MAX_LEN: number;
  DEFAULT_CUSTOMER_TERMINAL_TYPE: string;
  STORAGE_KEYS_BASE: StorageKeysBase;
  buildKeys(orderLib: Pick<RestaurantOrderLib, 'ORDERS_KEY'> | null | undefined): StorageKeys;
  PRINTER_MAPPING_EXAMPLE: PrinterMappingExample;
  DEFAULT_PRINTER_ROUTING: PrinterRouting;
  STORED_BUILTIN_PRINTER_NAME: string;
  DEFAULT_BUILTIN_PRINTER_ID: string;
  DEFAULT_PRINT_V2: PrintV2;
  getUiLabelsBundle(): UiLabelsBundle;
  getReceiptDataBundle(): ReceiptDataBundle;
}

/* ─────────── 全局 facade 局部断言 ─────────── */

interface RuntimeGlobal {
  RestaurantConstants?: RestaurantConstants;
  RestaurantAppConfig?: RestaurantAppConfig;
  buildRestaurantOsUiLabels?: () => UiLabelsBundle;
  /** app-receipt-data.js 注入的桥 */
  __OS_RECEIPT_DATA__?: Partial<ReceiptDataBundle>;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
  location?: Location;
}

const G = (typeof window !== 'undefined' ? window : globalThis) as unknown as RuntimeGlobal;

const RC0 = G.RestaurantConstants;

/* ─────────── 静态常量 ─────────── */

/** 文档/占位：可被 js/constants.js 中 RestaurantConstants 覆盖 */
const OS_LAN_IPV4 = (RC0 && RC0.OS_LAN_IPV4) || '192.168.100.112';

/** 与静态托管的 node server 同源时用 location.port；无端口时 http 默认与 RestaurantConstants.DEFAULT_HTTP_PORT 一致 */
const getDefaultThermalPrintEndpoint = (): string => {
  const defPort = String((RC0 && RC0.DEFAULT_HTTP_PORT) || 3000);
  try {
    const loc = G.location;
    if (loc && loc.hostname) {
      const proto = loc.protocol === 'https:' ? 'https:' : 'http:';
      let port = loc.port;
      if (!port) port = proto === 'https:' ? '443' : defPort;
      return `${proto}//${loc.hostname}:${port}/print`;
    }
  } catch {
    /* ignore：location 可能在沙箱里被禁 */
  }
  return `http://${OS_LAN_IPV4}:${defPort}/print`;
};

/** 与 config-lib UI_LANG_ALLOWED 顺序一致 */
const LANG_PICKER: readonly LangPickerEntry[] = [
  { code: 'de', abbr: 'DE', flag: 'assets/flags/de.svg' },
  { code: 'en', abbr: 'EN', flag: 'assets/flags/gb.svg' },
  { code: 'zh', abbr: 'ZH', flag: 'assets/flags/cn.svg' },
  { code: 'es', abbr: 'ES', flag: 'assets/flags/es.svg' },
  { code: 'fr', abbr: 'FR', flag: 'assets/flags/fr.svg' },
  { code: 'it', abbr: 'IT', flag: 'assets/flags/it.svg' },
  { code: 'ja', abbr: 'JP', flag: 'assets/flags/jp.svg' },
  { code: 'tr', abbr: 'TR', flag: 'assets/flags/tr.svg' },
];

const CUSTOMER_SESSION_STORAGE = 'restaurantCustomerSession';
const ORDER_COOLDOWN_UNTIL_KEY = 'restaurantOrderCooldownUntil';
const CUSTOMER_MEAL_TAB_KEY = 'restaurantCustomerMealTab';

const DEFAULT_CUSTOMER_GUEST_NAME = 'A';
const GUEST_NAME_MAX_LEN = 32;
/** 顾客端同桌共用终端时的默认设备标识（写入会话，便于小票/对账展示） */
const DEFAULT_CUSTOMER_TERMINAL_TYPE = 'ipad_mini';

/** 不含 ORDERS（由 order-lib 在运行时注入） */
const STORAGE_KEYS_BASE: StorageKeysBase = {
  MENU: 'restaurantMenu',
  MENU_PRESET_VERSION: 'restaurantMenuPresetVersion',
  PRINT_CONFIG: 'restaurantPrintConfig',
  PRINTER_ROUTING: 'restaurantPrinterRouting',
  NETWORK_CONFIG: 'restaurantNetworkConfig',
  RESTAURANT_INFO: 'restaurantInfo',
  UI_LANG: 'restaurantUiLang',
  PRINTER_SETTINGS: 'printerSettings',
  SYSTEM_SETTINGS: 'restaurantSystemSettings',
};

const buildKeys = (
  orderLib: Pick<RestaurantOrderLib, 'ORDERS_KEY'> | null | undefined,
): StorageKeys => {
  const ORDERS = orderLib && orderLib.ORDERS_KEY ? orderLib.ORDERS_KEY : 'restaurantOrders';
  return { ...STORAGE_KEYS_BASE, ORDERS };
};

/** 文档/管理端示例：分类 id → 人类可读打印机名（实际路由用 categoryToPrinterId） */
const PRINTER_MAPPING_EXAMPLE: PrinterMappingExample = {
  hot: '厨房打印机1',
  cold: '凉菜间打印机',
  drink: '吧台打印机',
  rice: '主食打印机',
  noodle: '面档打印机',
  snack: '小食打印机',
};

const DEFAULT_PRINTER_ROUTING: PrinterRouting = {
  dispatchMode: 'per_category',
  categoryToPrinterId: {},
};

const STORED_BUILTIN_PRINTER_NAME = '默认打印机';
const DEFAULT_BUILTIN_PRINTER_ID = 'p_default';

const DEFAULT_PRINT_V2: PrintV2 = {
  printers: [
    {
      id: DEFAULT_BUILTIN_PRINTER_ID,
      name: STORED_BUILTIN_PRINTER_NAME,
      paperWidth: '80',
      autoPrint: false,
      header: '',
      footer: '',
    },
  ],
  activePrinterId: 'p_default',
  orderReceipt: {
    receiptLang: 'de',
    receiptLangSecondary: '',
    fontPreset: 'mono',
  },
};

const getUiLabelsBundle = (): UiLabelsBundle => {
  if (typeof G.buildRestaurantOsUiLabels === 'function') {
    return G.buildRestaurantOsUiLabels();
  }
  console.error('[RestaurantAppConfig] 需先加载 ui-labels.bundle.js + js/constants.js（或 constants-loader）');
  /** 8 语种空兜底，与 UI_LANG_ALLOWED 对齐 */
  return { zh: {}, de: {}, en: {}, it: {}, es: {}, ja: {}, tr: {}, fr: {} } as UiLabelsBundle;
};

const getReceiptDataBundle = (): ReceiptDataBundle => {
  const b = G.__OS_RECEIPT_DATA__;
  if (!b) {
    console.error('[RestaurantAppConfig] 需先加载 app-receipt-data.js');
  }
  return {
    RECEIPT_LOCALE: (b && b.RECEIPT_LOCALE) || { zh: 'zh-CN' },
    RECEIPT_LABELS: (b && b.RECEIPT_LABELS) || { zh: {} },
    RECEIPT_FONT_CSS: (b && b.RECEIPT_FONT_CSS) || { mono: '', sans: '', large: '' },
  };
};

/* ─────────── 暴露 ─────────── */

const config: RestaurantAppConfig = {
  OS_LAN_IPV4,
  getDefaultThermalPrintEndpoint,
  LANG_PICKER,
  CUSTOMER_SESSION_STORAGE,
  ORDER_COOLDOWN_UNTIL_KEY,
  CUSTOMER_MEAL_TAB_KEY,
  DEFAULT_CUSTOMER_GUEST_NAME,
  GUEST_NAME_MAX_LEN,
  DEFAULT_CUSTOMER_TERMINAL_TYPE,
  STORAGE_KEYS_BASE,
  buildKeys,
  PRINTER_MAPPING_EXAMPLE,
  DEFAULT_PRINTER_ROUTING,
  STORED_BUILTIN_PRINTER_NAME,
  DEFAULT_BUILTIN_PRINTER_ID,
  DEFAULT_PRINT_V2,
  getUiLabelsBundle,
  getReceiptDataBundle,
};

G.RestaurantAppConfig = config;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('config', config);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
